import { Job, User } from '@core/generated/gql-types';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';

interface AssignedWorkerCardProps {
  job: Job;
  worker: User | null;
  loading: boolean;
  setOpenUnassignWorkerModal: (open: boolean) => void;
  allowUnassign?: boolean;
  showWorkerPayRate?: boolean;
}

const AssignedWorkerCard = ({
  job,
  worker,
  loading,
  setOpenUnassignWorkerModal,
  allowUnassign = true,
  showWorkerPayRate = true,
}: AssignedWorkerCardProps) => {
  const { getTerm } = useTemplateStore();

  const jobInPast = job?.startDate
    ? dayjs(parseInt(job.startDate, 10)).isBefore(dayjs())
    : true;

  if (loading) {
    return (
      <div className="flex h-14 w-full items-center justify-between gap-2 rounded-md bg-white p-2">
        <div className="flex items-center gap-2">
          <div className="pl-3 text-sm text-gray-600">
            Unassigning worker...
          </div>
        </div>
      </div>
    );
  }

  if (job.userId && !worker) {
    return (
      <div className="flex h-14 w-full items-center justify-between gap-2 rounded-md bg-white p-2">
        <div className="flex items-center gap-2">
          <div className="pl-3 text-sm text-gray-600">
            Cannot find the job's worker...
          </div>
        </div>
      </div>
    );
  }

  if (worker && job) {
    return (
      <div className="flex w-full items-center justify-between gap-2 rounded-md bg-white p-2">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center overflow-hidden rounded-full bg-gray-100">
            {worker?.profilePictureUrl ? (
              <img
                src={worker.profilePictureUrl}
                alt={worker?.firstName + ' ' + worker?.lastName}
                className="h-10 w-10 object-contain"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-200" />
            )}
          </div>
          <div className="flex flex-col">
            <p className="text-sm font-medium text-gray-900">{`${worker?.firstName} ${worker?.lastName}`}</p>
            <p className="text-sm text-gray-400">
              {job?.classification?.name || 'Job classification not set'}
            </p>
            {showWorkerPayRate && (
              <p className="text-sm text-gray-400">
                {job?.finalWorkerPayRateCents
                  ? `$${job.finalWorkerPayRateCents / 100}`
                  : 'Not set'}
              </p>
            )}
          </div>
        </div>
        {allowUnassign && !jobInPast && (
          <button
            type="button"
            onClick={async () => {
              setOpenUnassignWorkerModal(true);
            }}
            className={`cursor-pointer rounded p-1 transition-colors hover:bg-gray-200 ${jobInPast ? 'cursor-not-allowed opacity-50' : ''}`}
            aria-label={`Remove ${getTerm(TemplateTerm.WORKER, false)}`}
          >
            <FontAwesomeIcon icon={faTrashCan} className="h-6 w-6" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-14 w-full items-center justify-between gap-2 rounded-md bg-white p-2">
      <div className="flex items-center gap-2">
        <div className="pl-3 text-sm text-gray-600">
          No {getTerm(TemplateTerm.WORKER, false)} assigned
        </div>
      </div>
    </div>
  );
};

export default AssignedWorkerCard;
